import type {
  AccountingMetrics,
  DashboardPayload,
  InputField,
  MonthlyMetric,
  ProductivityMetrics,
  SummaryMetrics,
  WorkingCapitalMetrics,
} from "../types";

type ScenarioBenchmark = {
  target: { summary: SummaryMetrics; monthly: MonthlyMetric[] };
  actual: { summary: SummaryMetrics; monthly: MonthlyMetric[] };
};

export const safeDivide = (numerator: number, denominator: number): number | null =>
  denominator === 0 ? null : numerator / denominator;

export const inputValue = (inputs: InputField[], key: string): number =>
  inputs.find((item) => item.key === key)?.value ?? 0;

export function calculateManagementMetrics(
  summary: SummaryMetrics,
  variableCosts: number,
  fixedCosts: number,
  inputs: InputField[],
  annualNewCustomers: number,
  monthly: MonthlyMetric[] = [],
  benchmark?: ScenarioBenchmark,
): Pick<DashboardPayload, "accounting" | "productivity" | "workingCapital" | "automation" | "publicFramework"> {
  const depreciation = inputValue(inputs, "annualDepreciation");
  const nonOperatingIncome = inputValue(inputs, "nonOperatingIncome");
  const interestExpense = inputValue(inputs, "interestExpense");
  const extraordinaryIncome = inputValue(inputs, "extraordinaryIncome");
  const extraordinaryLoss = inputValue(inputs, "extraordinaryLoss");
  const taxes = inputValue(inputs, "taxes");
  const totalAssets = inputValue(inputs, "totalAssets");
  const equity = inputValue(inputs, "equity");
  const debt = inputValue(inputs, "interestBearingDebt");
  const cash = inputValue(inputs, "cash");
  const currentAssets = inputValue(inputs, "currentAssets");
  const currentLiabilities = inputValue(inputs, "currentLiabilities");
  const inventory = inputValue(inputs, "inventory");
  const receivables = inputValue(inputs, "receivables");
  const payables = inputValue(inputs, "payables");
  const b2bAcquisitionCost = inputValue(inputs, "b2bAcquisitionCost");
  const b2cAcquisitionCost = inputValue(inputs, "b2cAcquisitionCost");
  const acquisitionCost = b2bAcquisitionCost + b2cAcquisitionCost || inputValue(inputs, "acquisitionCost");
  const annualHours = inputValue(inputs, "annualHours");
  const fte = inputValue(inputs, "fte");
  const internalLaborCost = inputValue(inputs, "monthlyOfficerComp") * 12
    + inputValue(inputs, "monthlySalary") * 12
    + inputValue(inputs, "monthlyBenefits") * 12;

  const contributionMargin = summary.revenue - variableCosts;
  const contributionMarginRate = safeDivide(contributionMargin, summary.revenue) ?? 0;
  const ebitda = summary.operatingProfit + depreciation;
  const ordinaryProfit = summary.operatingProfit + nonOperatingIncome - interestExpense;
  const pretaxProfit = ordinaryProfit + extraordinaryIncome - extraordinaryLoss;
  const netProfit = pretaxProfit - taxes;
  const breakEvenSales = contributionMarginRate === 0 ? 0 : fixedCosts / contributionMarginRate;
  const marginOfSafetyRate = safeDivide(summary.revenue - breakEvenSales, summary.revenue) ?? 0;
  const cashFixedCost = Math.max(0, fixedCosts - depreciation);

  const accounting: AccountingMetrics = {
    contributionMargin,
    contributionMarginRate,
    ebitda,
    ordinaryProfit,
    pretaxProfit,
    netProfit,
    breakEvenSales,
    marginOfSafetyRate,
    roa: safeDivide(netProfit, totalAssets),
    roe: safeDivide(netProfit, equity),
    equityRatio: safeDivide(equity, totalAssets),
    deRatio: safeDivide(debt, equity),
    interestCoverage: safeDivide(summary.operatingProfit, interestExpense),
    currentRatio: safeDivide(currentAssets, currentLiabilities),
    quickRatio: safeDivide(currentAssets - inventory, currentLiabilities),
    cashMonths: safeDivide(cash, summary.revenue / 12),
    runway: safeDivide(cash, cashFixedCost / 12),
  };

  const productivity: ProductivityMetrics = {
    revenuePerHour: safeDivide(summary.revenue, annualHours),
    grossProfitPerHour: safeDivide(summary.grossProfit, annualHours),
    ebitdaPerHour: safeDivide(ebitda, annualHours),
    revenuePerFte: safeDivide(summary.revenue, fte),
    grossProfitPerFte: safeDivide(summary.grossProfit, fte),
    ebitdaPerFte: safeDivide(ebitda, fte),
    laborCostRatio: safeDivide(internalLaborCost, summary.revenue) ?? 0,
    laborDistributionRate: safeDivide(internalLaborCost, summary.grossProfit) ?? 0,
  };

  const dso = safeDivide(receivables * 365, summary.revenue);
  const dio = safeDivide(inventory * 365, summary.costOfSales);
  const dpo = safeDivide(payables * 365, summary.costOfSales);
  const workingCapital: WorkingCapitalMetrics = {
    dso: receivables === 0 ? null : dso,
    dio: inventory === 0 ? null : dio,
    dpo: payables === 0 ? null : dpo,
    ccc: dso === null || dio === null || dpo === null || receivables === 0 || inventory === 0 || payables === 0
      ? null
      : dso + dio - dpo,
    workingCapital: receivables === 0 && inventory === 0 && payables === 0 ? null : receivables + inventory - payables,
    assetTurnover: safeDivide(summary.revenue, totalAssets),
    cac: acquisitionCost === 0 ? null : safeDivide(acquisitionCost, annualNewCustomers),
    netDebt: debt === 0 && cash === 0 ? null : debt - cash,
  };

  const first = monthly.at(0);
  const last = monthly.at(-1);
  const annualizedMrrGrowth = first && last && first.mrr > 0 && monthly.length > 1
    ? (last.mrr / first.mrr) ** (12 / (monthly.length - 1)) - 1
    : null;
  const averageMonthlyRevenueGrowth = first && last && first.revenue > 0 && monthly.length > 1
    ? (last.revenue / first.revenue) ** (1 / (monthly.length - 1)) - 1
    : null;
  const b2bActiveMonths = monthly.reduce((sum, row) => sum + row.b2bActive, 0);
  const b2cActiveMonths = monthly.reduce((sum, row) => sum + row.b2cActive, 0);
  const b2bAnnualNew = monthly.reduce((sum, row) => sum + row.b2bNew, 0);
  const b2cAnnualNew = monthly.reduce((sum, row) => sum + row.b2cNew, 0);
  const b2bArpu = safeDivide(monthly.reduce((sum, row) => sum + row.b2bMrr, 0), b2bActiveMonths);
  const b2cArpu = safeDivide(monthly.reduce((sum, row) => sum + row.b2cMrr, 0), b2cActiveMonths);
  const b2bChurn = inputValue(inputs, "b2bChurn");
  const b2cChurn = inputValue(inputs, "b2cChurn");
  const b2bLifetimeMonths = b2bChurn > 0 ? 6 + 1 / b2bChurn : null;
  const b2cLifetimeMonths = b2cChurn > 0 ? 1 / b2cChurn : null;
  const b2bLtv = b2bArpu === null || b2bLifetimeMonths === null ? null : b2bArpu * summary.grossMargin * b2bLifetimeMonths;
  const b2cLtv = b2cArpu === null || b2cLifetimeMonths === null ? null : b2cArpu * summary.grossMargin * b2cLifetimeMonths;
  const b2bCac = b2bAcquisitionCost > 0 ? safeDivide(b2bAcquisitionCost, b2bAnnualNew) : null;
  const b2cCac = b2cAcquisitionCost > 0 ? safeDivide(b2cAcquisitionCost, b2cAnnualNew) : null;
  const b2bMonthlyGrossProfit = b2bArpu === null ? null : b2bArpu * summary.grossMargin;
  const b2cMonthlyGrossProfit = b2cArpu === null ? null : b2cArpu * summary.grossMargin;

  const endingWorkingCapital = receivables + inventory - payables;
  const openingWorkingCapital = inputValue(inputs, "openingReceivables")
    + inputValue(inputs, "openingInventory")
    - inputValue(inputs, "openingPayables");
  const workingCapitalChange = endingWorkingCapital - openingWorkingCapital;
  const operatingCashFlowProxy = ebitda - taxes - interestExpense - workingCapitalChange;
  const freeCashFlowProxy = operatingCashFlowProxy - inputValue(inputs, "annualCapex");
  const annualPrincipalRepayment = inputValue(inputs, "annualPrincipalRepayment");
  const debtService = annualPrincipalRepayment + interestExpense;
  const investedCapital = debt + equity - cash;
  const cashOperatingCosts = Math.max(0, summary.revenue - ebitda + taxes + interestExpense);

  const targetB2bNew = benchmark?.target.monthly.reduce((sum, row) => sum + row.b2bNew, 0) ?? 0;
  const targetB2cNew = benchmark?.target.monthly.reduce((sum, row) => sum + row.b2cNew, 0) ?? 0;
  const actualB2bNew = benchmark?.actual.monthly.reduce((sum, row) => sum + row.b2bNew, 0) ?? 0;
  const actualB2cNew = benchmark?.actual.monthly.reduce((sum, row) => sum + row.b2cNew, 0) ?? 0;
  const targetEbitda = benchmark ? benchmark.target.summary.operatingProfit + depreciation : 0;
  const actualEbitda = benchmark ? benchmark.actual.summary.operatingProfit + depreciation : 0;

  const automation = {
    growth: {
      arr: summary.mrr * 12,
      revenueRunRate: (last?.revenue ?? 0) * 12,
      recurringRevenueRate: safeDivide(summary.recurringRevenue, summary.revenue) ?? 0,
      annualizedMrrGrowth,
      averageMonthlyRevenueGrowth,
      ruleOf40: annualizedMrrGrowth === null ? null : annualizedMrrGrowth + summary.operatingMargin,
    },
    profitability: {
      ebitdaMargin: safeDivide(ebitda, summary.revenue) ?? 0,
      contributionMargin,
      contributionMarginRate,
      operatingLeverage: safeDivide(contributionMargin, summary.operatingProfit),
      breakEvenSales,
      marginOfSafetyRate,
      firstProfitableMonth: monthly.find((row) => row.operatingProfit > 0)?.month ?? null,
    },
    unitEconomics: {
      b2bArpu,
      b2cArpu,
      b2bLifetimeMonths,
      b2cLifetimeMonths,
      b2bLtv,
      b2cLtv,
      b2bCac,
      b2cCac,
      b2bLtvCac: b2bLtv === null || b2bCac === null ? null : safeDivide(b2bLtv, b2bCac),
      b2cLtvCac: b2cLtv === null || b2cCac === null ? null : safeDivide(b2cLtv, b2cCac),
      b2bPaybackMonths: b2bCac === null || b2bMonthlyGrossProfit === null ? null : safeDivide(b2bCac, b2bMonthlyGrossProfit),
      b2cPaybackMonths: b2cCac === null || b2cMonthlyGrossProfit === null ? null : safeDivide(b2cCac, b2cMonthlyGrossProfit),
    },
    cashFlow: {
      workingCapitalChange,
      operatingCashFlowProxy,
      freeCashFlowProxy,
      dscr: safeDivide(operatingCashFlowProxy, debtService),
      netDebtToEbitda: safeDivide(debt - cash, ebitda),
      roic: investedCapital > 0 ? safeDivide(summary.operatingProfit - taxes, investedCapital) : null,
      cashDays: cash > 0 ? safeDivide(cash * 365, cashOperatingCosts) : null,
      cashConversionRate: safeDivide(freeCashFlowProxy, ebitda),
    },
    variance: {
      revenueAttainment: benchmark ? safeDivide(benchmark.actual.summary.revenue, benchmark.target.summary.revenue) : null,
      operatingProfitAttainment: benchmark ? safeDivide(benchmark.actual.summary.operatingProfit, benchmark.target.summary.operatingProfit) : null,
      ebitdaAttainment: benchmark ? safeDivide(actualEbitda, targetEbitda) : null,
      b2bNewAttainment: benchmark ? safeDivide(actualB2bNew, targetB2bNew) : null,
      b2cNewAttainment: benchmark ? safeDivide(actualB2cNew, targetB2cNew) : null,
      yearEndActiveAttainment: benchmark ? safeDivide(benchmark.actual.summary.yearEndActive, benchmark.target.summary.yearEndActive) : null,
      mrrAttainment: benchmark ? safeDivide(benchmark.actual.summary.mrr, benchmark.target.summary.mrr) : null,
    },
  };

  const priorYearRevenue = inputValue(inputs, "priorYearRevenue");
  const localSalesGrowth = priorYearRevenue > 0 ? summary.revenue / priorYearRevenue - 1 : null;
  const localOperatingMargin = summary.revenue > 0 ? summary.operatingProfit / summary.revenue : null;
  const localLaborProductivity = fte > 0 ? summary.operatingProfit / fte : null;
  const localDebtMultiple = (debt !== 0 || cash !== 0) && ebitda !== 0 ? (debt - cash) / ebitda : null;
  const localWorkingCapitalMonths = (receivables !== 0 || inventory !== 0 || payables !== 0) && summary.revenue > 0
    ? (receivables + inventory - payables) / (summary.revenue / 12)
    : null;
  const localEquityRatio = totalAssets > 0 ? equity / totalAssets : null;
  const localMetrics = [
    localSalesGrowth,
    localOperatingMargin,
    localLaborProductivity,
    localDebtMultiple,
    localWorkingCapitalMonths,
    localEquityRatio,
  ];

  const nonFinancialScores = {
    management: inputValue(inputs, "managementMaturity"),
    business: inputValue(inputs, "businessMaturity"),
    stakeholders: inputValue(inputs, "stakeholderMaturity"),
    internalControl: inputValue(inputs, "internalControlMaturity"),
  };
  const scoreValues = Object.values(nonFinancialScores);
  const nonFinancialAverage = scoreValues.some((score) => score > 0)
    ? scoreValues.reduce((sum, score) => sum + score, 0) / scoreValues.length
    : null;

  const effectiveTaxRate = inputValue(inputs, "effectiveTaxRate");
  const waccInput = inputValue(inputs, "wacc");
  const nopat = summary.operatingProfit * (1 - effectiveTaxRate);
  const publicInvestedCapital = debt !== 0 || equity !== 0 || cash !== 0 ? debt + equity - cash : null;
  const publicRoic = publicInvestedCapital !== null && publicInvestedCapital > 0 ? nopat / publicInvestedCapital : null;
  const publicWacc = waccInput > 0 ? waccInput : null;
  const roicWaccSpread = publicRoic !== null && publicWacc !== null ? publicRoic - publicWacc : null;
  const economicProfit = publicInvestedCapital !== null && publicWacc !== null
    ? nopat - publicInvestedCapital * publicWacc
    : null;
  const humanCapitalInvestment = inputValue(inputs, "humanCapitalInvestment");
  const dxRdInvestment = inputValue(inputs, "dxRdInvestment");
  const healthInvestment = inputValue(inputs, "healthInvestment");
  const capex = inputValue(inputs, "annualCapex");
  const growthInvestment = humanCapitalInvestment + capex + dxRdInvestment + healthInvestment;

  const healthIssueActions = inputValue(inputs, "healthIssueActions");
  const foundationActions = inputValue(inputs, "foundationActions");
  const promotionActions = inputValue(inputs, "promotionActions");
  const healthChecks = [
    { id: "policy", label: "経営理念・方針", met: inputValue(inputs, "healthDeclaration") >= 1 && inputValue(inputs, "executiveHealthCheck") >= 1, requirement: "健康宣言の発信と経営者の健診受診" },
    { id: "organization", label: "組織体制", met: inputValue(inputs, "healthLeader") >= 1 && inputValue(inputs, "healthDataReady") >= 1, requirement: "担当者の設置と40歳以上の健診データ提供体制" },
    { id: "plan", label: "制度・施策実行", met: inputValue(inputs, "healthPlan") >= 1, requirement: "健康経営の具体的推進計画" },
    { id: "issues", label: "健康課題の把握", met: healthIssueActions >= 2, requirement: "公開要件①〜③のうち2項目以上" },
    { id: "foundation", label: "土台づくり", met: foundationActions >= 2, requirement: "公開要件④〜⑩のうち2項目以上" },
    { id: "promotion", label: "心身の健康づくり", met: promotionActions >= 4, requirement: "公開要件⑪〜⑰のうち4項目以上" },
    { id: "smoking", label: "受動喫煙対策", met: inputValue(inputs, "passiveSmokingControl") >= 1, requirement: "受動喫煙対策の実施" },
    { id: "review", label: "評価・改善", met: inputValue(inputs, "healthReview") >= 1, requirement: "取組効果の評価と改善" },
    { id: "compliance", label: "法令遵守", met: inputValue(inputs, "healthCompliance") >= 1, requirement: "法令遵守・リスク管理" },
  ];
  const checksMet = healthChecks.filter((check) => check.met).length;
  const readinessRate = checksMet / healthChecks.length;

  const publicFramework = {
    localBenchmark: {
      salesGrowthRate: localSalesGrowth,
      operatingMargin: localOperatingMargin,
      laborProductivity: localLaborProductivity,
      ebitdaDebtMultiple: localDebtMultiple,
      workingCapitalMonths: localWorkingCapitalMonths,
      equityRatio: localEquityRatio,
      completenessRate: localMetrics.filter((metric) => metric !== null).length / localMetrics.length,
    },
    nonFinancial: { ...nonFinancialScores, average: nonFinancialAverage },
    valueCreation: {
      nopat,
      investedCapital: publicInvestedCapital,
      roic: publicRoic,
      wacc: publicWacc,
      roicWaccSpread,
      economicProfit,
      humanCapitalInvestment,
      capex,
      dxRdInvestment,
      healthInvestment,
      growthInvestment,
      growthInvestmentRate: safeDivide(growthInvestment, summary.revenue),
      humanInvestmentPerFte: safeDivide(humanCapitalInvestment, fte),
      trainingHoursPerFte: safeDivide(inputValue(inputs, "annualTrainingHours"), fte),
      capexDepreciation: safeDivide(capex, depreciation),
      wageGrowthRate: inputValue(inputs, "wageGrowthRate"),
      turnoverRate: inputValue(inputs, "voluntaryTurnoverRate"),
      engagementScore: inputValue(inputs, "engagementScore"),
    },
    healthManagement: {
      readinessRate,
      checksMet,
      checksTotal: healthChecks.length,
      status: checksMet === healthChecks.length ? "主要区分入力済み（参考）" : checksMet >= 6 ? "要件を再確認" : "準備中",
      checks: healthChecks,
      healthIssueActions,
      foundationActions,
      promotionActions,
      healthCheckRate: inputValue(inputs, "healthCheckRate"),
      stressCheckRate: inputValue(inputs, "stressCheckRate"),
      paidLeaveRate: inputValue(inputs, "paidLeaveRate"),
      overtimeHours: inputValue(inputs, "overtimeHours"),
      healthInvestmentPerFte: safeDivide(healthInvestment, fte),
    },
  };

  return { accounting, productivity, workingCapital, automation, publicFramework };
}
